import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Collapse
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getServiceName } from '../../utils/serviceTranslations';
import { formatCurrency } from '../../utils/currencyUtils';
import {
  ServiceValueMode,
  formatServiceDurationLabel,
  formatServicePriceLabel,
  normalizeServiceValueMode,
} from '../../utils/serviceValueFormat';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const TeamMemberServices = ({ formData, updateFormData, services, errors, currency = 'EUR' }) => {
  const { t, i18n } = useTranslation();
  const [expandedServices, setExpandedServices] = useState(new Set());

  // Helper function to format price
  const formatPrice = (price) => {
    if (price == null || price === '') return formatCurrency(0, currency, { showDecimals: false });
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return formatCurrency(0, currency, { showDecimals: false });
    return formatCurrency(numPrice, currency);
  };

  const handleServiceToggle = (serviceId) => {
    const newServices = formData.services || [];
    const existingServiceIndex = newServices.findIndex(s => s.serviceId === serviceId);
    
    if (existingServiceIndex >= 0) {
      // Remove service
      const updatedServices = newServices.filter(s => s.serviceId !== serviceId);
      updateFormData({ services: updatedServices });
    } else {
      // Add service with default values
      const newService = {
        serviceId: serviceId,
        customPrice: null
      };
      updateFormData({ services: [...newServices, newService] });
    }
  };

  const handleCustomPriceChange = (serviceId, value) => {
    const newServices = (formData.services || []).map(service => 
      service.serviceId === serviceId 
        ? { ...service, customPrice: value ? parseFloat(value) : null }
        : service
    );
    updateFormData({ services: newServices });
  };

  const isServiceSelected = (serviceId) => {
    return (formData.services || []).some(s => s.serviceId === serviceId);
  };

  const getSelectedService = (serviceId) => {
    return (formData.services || []).find(s => s.serviceId === serviceId);
  };

  const toggleCustomSettings = (serviceId) => {
    const newExpanded = new Set(expandedServices);
    if (newExpanded.has(serviceId)) {
      newExpanded.delete(serviceId);
    } else {
      newExpanded.add(serviceId);
    }
    setExpandedServices(newExpanded);
  };

  const isCustomSettingsExpanded = (serviceId) => {
    return expandedServices.has(serviceId);
  };


  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, color: BRAND.text, fontWeight: 600 }}>
        {t('teamMembers.registration.steps.step3.title')}
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
        {t('teamMembers.registration.steps.step3.description')}
      </Typography>

      {services.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
          {t('teamMembers.registration.noServices')}
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {services.map((service) => {
            const isSelected = isServiceSelected(service.id);
            const selectedService = getSelectedService(service.id);
            
            return (
              <Grid size={{ xs: 12, lg: 6 }} key={service.id}>
                <Box
                  sx={{
                    p: 3,
                    border: `2px solid ${isSelected ? BRAND.accent : BRAND.line}`,
                    borderRadius: '12px',
                    backgroundColor: isSelected ? brandAlpha('accent', 0.08) : BRAND.surface,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: BRAND.accent,
                      backgroundColor: isSelected ? brandAlpha('accent', 0.08) : BRAND.background
                    }
                  }}
                >
                {/* Service Selection */}
                <Box
                  onClick={() => handleServiceToggle(service.id)}
                  sx={{
                    cursor: 'pointer',
                    mb: isSelected ? 2 : 0
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {getServiceName(service.name, i18n.language)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('teamMembers.registration.defaultPrice')}: {formatServicePriceLabel(service, {
                      formatMoney: formatPrice,
                      onRequestLabel: t('servicesManagement.valueMode.onRequest'),
                      fromLabel: (price) => t('servicesManagement.valueMode.priceFrom', { price }),
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('teamMembers.registration.duration')}: {formatServiceDurationLabel(service, {
                      onRequestLabel: t('servicesManagement.valueMode.onRequest'),
                    })}
                  </Typography>
                </Box>

                {/* Custom Price and Duration Fields */}
                {isSelected && normalizeServiceValueMode(service.priceMode) === ServiceValueMode.Fixed && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${BRAND.line}` }}>
                    <Box
                      onClick={() => toggleCustomSettings(service.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        mb: isCustomSettingsExpanded(service.id) ? 2 : 0
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {t('teamMembers.registration.customSettings')}
                      </Typography>
                      {isCustomSettingsExpanded(service.id) ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </Box>
                    <Collapse in={isCustomSettingsExpanded(service.id)}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('teamMembers.registration.customPrice')}
                      </Typography>
                      <input
                        type="number"
                        value={selectedService?.customPrice || ''}
                        onChange={(e) => handleCustomPriceChange(service.id, e.target.value)}
                        placeholder={`${service.price} (${t('teamMembers.registration.default')})`}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: `2px solid ${BRAND.line}`,
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontFamily: 'inherit',
                          outline: 'none',
                          transition: 'border-color 0.3s ease',
                          backgroundColor: '#fff'
                        }}
                        onFocus={(e) => e.target.style.borderColor = BRAND.accent}
                        onBlur={(e) => e.target.style.borderColor = BRAND.line}
                      />
                      <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
                        {t('teamMembers.registration.customPriceHelp')}
                      </Typography>
                    </Box>
                    </Collapse>
                  </Box>
                )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {errors.services && (
        <Typography variant="caption" sx={{ color: '#f44336', mt: 2, display: 'block' }}>
          {errors.services}
        </Typography>
      )}
    </Box>
  );
};

export default TeamMemberServices;
